import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { getToken, getRefreshToken } from '../authentication/authProvider';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

/** Stages a lead can be reset out of. closed_won is a case — never reset. */
export const RESETTABLE_STAGES = ['qualified', 'disqualified', 'closed_lost', 'spam'];

const STAGE_LABELS: Record<string, string> = {
  qualified: 'Qualified',
  disqualified: 'Disqualified',
  closed_lost: 'Closed lost',
  spam: 'Spam',
};

export function resetStageDialogCopy(stage: string) {
  const label = STAGE_LABELS[stage] || stage;
  return {
    title: 'Reset stage?',
    body: `Are you sure? This clears ${label} so you can set it again.`,
    // A qualified lead that didn't go ahead is closed lost, not un-qualified —
    // so for Qualified the dialog steers there first.
    closeLostHint:
      stage === 'qualified'
        ? 'A qualified lead that didn’t go ahead is usually marked Closed lost rather than reset. Reset only if qualifying it was a mistake.'
        : null,
    confirmLabel: stage === 'qualified' ? 'Reset anyway' : 'Reset stage',
  };
}

/**
 * "Reset stage" in the lead's stage control. Clears a terminal stage so staff
 * can set it again (POST /leads/:id/reset-stage). Disabled when the lead is
 * already linked to a case — the server refuses those too.
 */
export function LeadResetStageButton({
  linkedFuneralId,
  onCloseLost,
}: {
  linkedFuneralId?: number | null;
  onCloseLost?: () => void;
}) {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);

  if (!record || !RESETTABLE_STAGES.includes(record.stage)) return null;

  const blocked = !!linkedFuneralId;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={blocked}
        title={blocked ? `Linked to case #${linkedFuneralId} — can’t be reset` : undefined}
        className="text-[13px] text-muted-foreground hover:text-foreground font-medium border border-border rounded px-2.5 py-1 hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Reset stage
      </button>
      <LeadResetStageDialog
        open={open}
        onClose={() => setOpen(false)}
        leadId={String(record.id)}
        stage={record.stage}
        onCloseLost={onCloseLost}
      />
    </>
  );
}

export function LeadResetStageDialog({
  open,
  onClose,
  leadId,
  stage,
  onCloseLost,
}: {
  open: boolean;
  onClose: () => void;
  leadId: string;
  stage: string;
  onCloseLost?: () => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [submitting, setSubmitting] = useState(false);
  const copy = resetStageDialogCopy(stage);
  const offerCloseLost = !!copy.closeLostHint && !!onCloseLost;

  const handleReset = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${baseUrl}/leads/${leadId}/reset-stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`,
          'Refresh-Token': await getRefreshToken(),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed (${response.status})`);
      }
      notify('Stage reset — you can set it again', { type: 'success' });
      onClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to reset stage', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{copy.body}</DialogContentText>
        {copy.closeLostHint && (
          <Alert severity="info" sx={{ mt: 2 }}>{copy.closeLostHint}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        {offerCloseLost ? (
          <>
            <Button variant="outlined" color="warning" onClick={handleReset} disabled={submitting}>
              {submitting ? 'Resetting...' : copy.confirmLabel}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={submitting}
              onClick={() => { onClose(); onCloseLost?.(); }}
            >
              Close lost
            </Button>
          </>
        ) : (
          <Button variant="contained" color="warning" onClick={handleReset} disabled={submitting}>
            {submitting ? 'Resetting...' : copy.confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
